<template>
  <ion-modal :is-open="updateAvailable" backdrop-dismiss="false" css-class="centered-modal">
  <div class="modal-wrapper">
    <div class="modal-content compact">
      <!-- AGB Box -->
      <ion-card class="compact-card">
        <ion-card-header>
          <ion-label class="section-title">{{ $t('Agb') }}</ion-label>
        </ion-card-header>
        <ion-card-content class="scroll-box">
          <p>{{ CIOVersion.agb }}</p>
        </ion-card-content>
      </ion-card>

      <!-- Datenschutz Box -->
      <ion-card class="compact-card">
        <ion-card-header>
          <ion-label class="section-title">{{ $t('datalegal') }}</ion-label>
        </ion-card-header>
        <ion-card-content class="scroll-box">
          <p>{{ CIOVersion.datalegal }}</p>
        </ion-card-content>
      </ion-card>

      <!-- Zustimmung -->
      <ion-grid class="checkbox-grid">
        <ion-row class="checkbox-line">
          <ion-col size="auto" class="checkbox-col">
            <ion-checkbox :checked="accepted" @ionChange="onAcceptedChange" color="primary" class="small-checkbox"></ion-checkbox>
          </ion-col>
          <ion-col class="label-col">
            <ion-label class="ion-text-wrap checkbox-label">{{ $t('acceptAgb') }}</ion-label>
          </ion-col>
        </ion-row>

        <ion-row class="checkbox-line">
          <ion-col size="auto" class="checkbox-col">
            <ion-checkbox :checked="accepted1" @ionChange="onAccepted1Change" color="primary" class="small-checkbox"></ion-checkbox>
          </ion-col>
          <ion-col class="label-col">
            <ion-label class="ion-text-wrap checkbox-label">{{ $t('acceptDSGVO') }}</ion-label>
          </ion-col>
        </ion-row>
      </ion-grid>

      <!-- Button -->
      <ion-button
        expand="block"
        color="primary"
        :disabled="!!isDisabled"
        @click="accept"
        class="ok-button"
        ref="okButton"
      >
        {{ $t('ok') }}
      </ion-button>
    </div>
  </div>
</ion-modal>

</template>

<script>
import { ref, onMounted, onUnmounted, computed, nextTick, watch } from 'vue'
import { App } from '@capacitor/app'
import { toastController, IonModal, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonButton } from '@ionic/vue'
import getPrivacyStatusQuery from '../graphql/getPrivacyStatus.query.gql'
import acceptPrivacyMutation from '../graphql/acceptPrivacy.mutation.gql'
import { useQuery, useMutation } from '@vue/apollo-composable'
import translate from '@/plugins/translate';
import { isPlatform } from '@ionic/vue';
import { useRouter } from 'vue-router';

export default {
  name: 'PrivacyChecker',
  components: { IonModal, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonButton },

  setup() {
    const updateAvailable = ref(false)
    let listener = null
    const accepted = ref(false)
    const accepted1 = ref(false)
    const okButton = ref(null)
    const isDisabled = computed(() => !accepted.value || !accepted1.value)
    const router = useRouter()

    const { result: getPrivacyStatusQueryResult, loading, refetch: getPrivacyStatusQueryRefetch } = useQuery(getPrivacyStatusQuery, null, { fetchPolicy: 'no-cache' })
    const CIOVersion = computed(() => getPrivacyStatusQueryResult.value?.getPrivacyStatus ?? '')

    const { mutate: acceptPrivacy } = useMutation(acceptPrivacyMutation, {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all'
    })

    async function checkForUpdate() {
      try {
        getPrivacyStatusQueryRefetch().then(() => {
          const ps = getPrivacyStatusQueryResult.value?.getPrivacyStatus
          if (ps && ps.mustAccept == true) {
            updateAvailable.value = true
          }
        }
      )
      } catch (err) {
        //console.log('[UpdateChecker] Fehler beim Versionscheck:', err)
      }
    }

    // 🔧 Force update on Ionic WebComponent when prop changes
    watch(isDisabled, async (val) => {
      await nextTick()
      if (okButton.value && okButton.value.$el) {
        okButton.value.$el.disabled = val
      }
    })
    function onAcceptedChange(e) {
      accepted.value = e.detail.checked
    }

    function onAccepted1Change(e) {
      accepted1.value = e.detail.checked
    }

    async function accept() {
      try {
        const { data, errors } = await acceptPrivacy({ version: CIOVersion.value.version })
        if (errors && errors.length) {
          ;(await toastController.create({
            message: translate('acceptError'),
            duration: 2000,
            color: 'danger'
          })).present()
          return
        }
        const ok = data?.acceptPrivacy === true
        if (ok) {
          updateAvailable.value = false
          accepted.value = false
          accepted1.value = false
          ;(await toastController.create({
            message: translate('acceptSuccess'),
            duration: 2000,
            color: 'success'
          })).present()
        } else {
          ;(await toastController.create({
            message: translate('acceptRetry'),
            duration: 2000,
            color: 'warning'
          })).present()
        }
      } catch (e) {
        ;(await toastController.create({
          message: translate('acceptNetworkError'),
          duration: 2000,
          color: 'danger'
        })).present()
      }
    }

    onMounted(async () => {
      // --- Router-Hook: bei jedem Screen-Wechsel mit Throttle prüfen ---
      router.afterEach(() => {
        checkForUpdate()
      })

      if (isPlatform('capacitor')) {
        listener = App.addListener('appStateChange', (isActive) => {
          if (isActive && !loading.value) checkForUpdate()
        })
      } else {
        if (!document.hidden) {
          checkForUpdate()
        }
        // Web-Fallback
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) checkForUpdate()
        })
      }
    })

    onUnmounted(() => {
      if (listener) listener.remove()
    })

    return { updateAvailable, accepted, accepted1, accept, CIOVersion, onAcceptedChange, onAccepted1Change, isDisabled, okButton }
  }
}
</script>

<style scoped>

.centered-modal::part(content) {
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  box-shadow: none;
  height: 100%;
  width: 100%;
}

/* Zentrierter Container */
.modal-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

/* Hauptinhalt – kompakt */
.modal-content.compact {
  background: var(--ion-background-color, #fff);
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  padding: 14px 16px;
  width: 90%;
  max-width: 480px;
  max-height: 85vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px; /* engerer Abstand zwischen Bereichen */
}

/* Kompakte Cards */
.compact-card {
  margin: 0;
  box-shadow: none;
  border: 1px solid var(--ion-color-light, #e0e0e0);
}

.section-title {
  font-weight: bold;
  font-size: 14px;
}

/* Scrollbare Textbereiche */
.scroll-box {
  max-height: 130px;
  overflow-y: auto;
  padding-right: 4px;
  font-size: 13px;
  line-height: 1.35;
  white-space: pre-wrap;
}

/* Checkbox-Gitter kompakter */
.checkbox-grid {
  margin-top: 4px;
}

.checkbox-line {
  align-items: flex-start;
  margin-bottom: 4px;
}

.checkbox-col {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding-top: 2px;
}

.small-checkbox {
  transform: scale(0.8);
  transform-origin: top left;
  margin-top: 2px;
}

.label-col {
  padding-left: 6px;
}

.checkbox-label {
  font-size: 13px;
  line-height: 1.3;
  white-space: normal;
  overflow-wrap: break-word;
}

/* Button enger am unteren Rand */
.ok-button {
  margin-top: 8px;
  margin-bottom: 4px;
  font-size: 14px;
  height: 34px;
}

</style>
